import { trackOutputAction } from 'analytics';
import forge from 'apis/forge';
import Modal from 'components/Modal';
import type { ExportCodeFramework } from 'dragonmouse-shared/api/forge';
import { MAX_LLM_EXPORT_FRAMES } from 'dragonmouse-shared/api/export';
import { Frame } from 'dragonmouse-shared/interfaces';
import { runExportWithToast } from 'components/Widget/ExportReadyToast';
import { useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectActiveScene, selectFrames } from 'redux/db/reducers';
import { hideExportFramesCodeModal } from 'redux/mode/actions';
import { RootReducer, selectSelectedFrames } from 'redux/reducers';
import store from 'redux/store';
import { buildExportFilename } from 'utils';
import * as Sentry from '@sentry/react';
import RadioCard from 'components/Widget/RadioCard';
import { ExportScope, resolveFrameIds, scopeText } from '../util';
import * as Shared from '../styles';
import * as S from './styles';

interface Props {
	fixedFrameIds?: string[];
	onClose: () => void;
}

// 'Custom' is a UI-only sentinel — on the wire we send either a known ExportCodeFramework
// or a user-typed string. `satisfies` enforces that every non-Custom entry is a real
// ExportCodeFramework; `Framework` is then derived so the names live in exactly one place.
const FRAMEWORK_CARDS = [
	{ framework: 'React + Tailwind', description: 'React with utility classes' },
	{ framework: 'Next.js + Tailwind', description: 'Next.js App Router' },
	{ framework: 'Plain HTML/CSS', description: 'Static HTML and CSS' },
	{ framework: 'Custom', description: 'Specify any framework' },
] as const satisfies ReadonlyArray<{
	framework: ExportCodeFramework | 'Custom';
	description: string;
}>;

type Framework = (typeof FRAMEWORK_CARDS)[number]['framework'];

const FramesCodeModal = ({ fixedFrameIds, onClose }: Props) => {
	const { selectedFrames, allFrames, activeScene } = useSelector(
		(r: RootReducer) => ({
			selectedFrames: selectSelectedFrames(r),
			allFrames: selectFrames(r.db),
			activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
		}),
		shallowEqual
	);

	const initialScope: ExportScope = selectedFrames.length > 0 ? 'selected' : 'all';
	const [scope] = useState<ExportScope>(initialScope);
	const [framework, setFramework] = useState<Framework>('React + Tailwind');
	const [customFramework, setCustomFramework] = useState('');
	const [customFrameworkInstructions, setCustomFrameworkInstructions] = useState('');
	const [submitted, setSubmitted] = useState(false);

	const activeFrames: Frame[] = useMemo(() => {
		const ids = resolveFrameIds(fixedFrameIds, scope, selectedFrames, allFrames);
		return ids.map((id) => allFrames.find((f) => f.id === id)).filter((f): f is Frame => !!f);
	}, [fixedFrameIds, scope, selectedFrames, allFrames]);

	const frameCount = activeFrames.length;
	const isPlainHtml = framework === 'Plain HTML/CSS';
	const overFrameCap = !isPlainHtml && frameCount > MAX_LLM_EXPORT_FRAMES;

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const closeModal = () => {
		store.dispatch(hideExportFramesCodeModal());
		onClose();
	};

	const onGenerate = async () => {
		if (frameCount === 0) return;
		if (framework === 'Custom' && !customFramework.trim()) {
			toast.error('Enter a framework name');
			return;
		}
		if (framework === 'Custom' && !customFrameworkInstructions.trim()) {
			toast.error('Describe how the project should be scaffolded');
			return;
		}
		if (overFrameCap) return;
		setSubmitted(true);
		trackOutputAction('export_frames_code');
		closeModal();

		if (isPlainHtml) {
			const filename = buildExportFilename(activeScene?.title, 'html', 'project');
			await runExportWithToast({
				pendingLabel: 'Packaging HTML…',
				errorLabel: 'Export failed. Please try again.',
				run: async () => {
					const { exportId } = await forge.exportFramesAssets({
						frameIds: activeFrames.map((f) => f.id),
						format: 'html+images',
						filename,
					});
					return { exportId, filename };
				},
			}).catch((e) => Sentry.captureException(e));
		} else {
			const isCustom = framework === 'Custom';
			const wireFramework = isCustom ? customFramework.trim() : framework;
			const filename = buildExportFilename(activeScene?.title, wireFramework, 'project');
			await runExportWithToast({
				pendingLabel: `Generating your ${wireFramework} project — this may take a few minutes…`,
				errorLabel: 'Export failed. Please try again.',
				run: async () => {
					const { exportId } = await forge.exportFramesCode({
						frameIds: activeFrames.map((f) => f.id),
						framework: wireFramework,
						customInstructions: isCustom ? customFrameworkInstructions.trim() : undefined,
						filename,
					});
					return { exportId, filename };
				},
			}).catch((e) => Sentry.captureException(e));
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const submitDisabled =
		submitted ||
		frameCount === 0 ||
		overFrameCap ||
		(framework === 'Custom' && (!customFramework.trim() || !customFrameworkInstructions.trim()));

	const submitLabel = 'Download';

	const footerInfo = !fixedFrameIds ? (
		<div>{scopeText(selectedFrames.length, allFrames.length)}</div>
	) : undefined;

	return (
		<Modal
			title="Export Code"
			onClose={closeModal}
			onCancel={closeModal}
			onSubmit={onGenerate}
			submitLabel={submitLabel}
			disabled={submitDisabled}
			loading={false}
			footerInfo={footerInfo}
		>
			<Shared.Body>
				<S.FrameworkGrid>
					{FRAMEWORK_CARDS.map((card) => (
						<RadioCard
							key={card.framework}
							active={framework === card.framework}
							label={card.framework}
							description={card.description}
							onClick={() => setFramework(card.framework)}
						/>
					))}
				</S.FrameworkGrid>
				{framework === 'Custom' && (
					<Shared.OptionsGroup>
						<Shared.CustomFrameworkInput
							placeholder="Framework name — e.g. SwiftUI, Svelte, Angular"
							value={customFramework}
							onChange={(e) => setCustomFramework(e.target.value)}
						/>
						<Shared.CustomFrameworkInstructions
							placeholder="How should the project be scaffolded? Describe the build tool, config files, routing, styling approach, and entry files the LLM should produce."
							value={customFrameworkInstructions}
							onChange={(e) => setCustomFrameworkInstructions(e.target.value)}
						/>
					</Shared.OptionsGroup>
				)}
			</Shared.Body>
		</Modal>
	);
};

export default FramesCodeModal;
